/**
 * Everything "+ Create" can start: each one an EXISTING create screen, behind
 * the same permission its route is guarded by in AppRoutes.jsx.
 *
 * Nothing here is new behaviour — it is a shorter way to the forms the list
 * pages already link to. Creates that exist only as a dialog on another page
 * (adding a contact to a supplier, raising a return from an order, and the
 * like) are deliberately absent: they need the record they belong to, and
 * are started from it.
 *
 * `app` places the action under its app, and hides it while that app's
 * optional module is switched off.
 */
import { registryKey } from './apps.js';

/**
 * "Purchase order" -> "New purchase order", "RFQ" -> "New RFQ": the first
 * word is lowered for the sentence unless it is an acronym.
 */
export function newLabel(label) {
  const [first, ...rest] = String(label).split(' ');
  const word = first === first.toUpperCase() ? first : first.charAt(0).toLowerCase() + first.slice(1);
  return ['New', word, ...rest].join(' ');
}

/**
 * What an action is called in the interface language, at render time (the
 * list below stays in English: it is the fallback and what search matches on).
 * Keyed by the action's path: navigation:create.<path>.label / .new — whole
 * phrases, because "New purchase order" is not "New" + a noun in Arabic.
 */
export function createLabel(action, t, options) {
  return t(`navigation:create.${registryKey(action.to)}.label`, { defaultValue: action.label, ...options });
}

/** "New purchase order", in the interface language. */
export function createNewLabel(action, t, options) {
  return t(`navigation:create.${registryKey(action.to)}.new`, { defaultValue: newLabel(action.label), ...options });
}

export const CREATE_ACTIONS = [
  { app: 'procurement', label: 'Purchase request', to: '/purchase-requests/new', icon: 'purchase-request', permissions: ['purchase.create'] },
  { app: 'procurement', label: 'RFQ', to: '/rfqs/new', icon: 'rfq', permissions: ['rfq.create'], keywords: 'request for quotation' },
  { app: 'procurement', label: 'Purchase order', to: '/purchase-orders/new', icon: 'purchase-order', permissions: ['purchase.create'], keywords: 'po' },
  { app: 'procurement', label: 'Vendor bill', to: '/purchase-bills/new', icon: 'bill', permissions: ['purchase.bill.create'] },
  { app: 'procurement', label: 'Supplier', to: '/suppliers/new', icon: 'supplier', permissions: ['supplier.create'], keywords: 'vendor' },

  { app: 'inventory', label: 'Delivery', to: '/inventory/deliveries/new', icon: 'delivery', permissions: ['inventory.delivery.create'] },
  { app: 'inventory', label: 'Transfer', to: '/inventory/transfers/new', icon: 'transfer', permissions: ['inventory.transfer.create'] },
  { app: 'inventory', label: 'Stock adjustment', to: '/inventory/adjustments/new', icon: 'adjustment', permissions: ['inventory.adjustment.create'] },
  { app: 'inventory', label: 'Stock count', to: '/inventory/counts/new', icon: 'count', permissions: ['inventory.count.create'] },
  { app: 'inventory', label: 'Landed cost', to: '/inventory/landed-costs/new', icon: 'landed-cost', permissions: ['inventory.landed-cost.create'] },

  { app: 'sales', label: 'Quotation', to: '/quotations/new', icon: 'quotation', permissions: ['sales.quote.create'], keywords: 'quote' },

  { app: 'finance', label: 'Journal entry', to: '/accounting/journals/new', icon: 'journal', permissions: ['accounting.journal.create', 'accounting.create'] },

  { app: 'crm', label: 'Lead', to: '/crm/leads/new', icon: 'lead', permissions: ['crm.lead.create'] },
  { app: 'crm', label: 'Opportunity board', to: '/crm/opportunities/new-board', icon: 'kanban', permissions: ['crm.opportunity.create'] },

  { app: 'people', label: 'Employee', to: '/employees/new', icon: 'employee', permissions: ['hr.create'] },
  { app: 'people', label: 'Department', to: '/departments/new', icon: 'department', permissions: ['hr.create'] },
  { app: 'people', label: 'Designation', to: '/designations/new', icon: 'designation', permissions: ['hr.create'] },

  { app: 'master-data', label: 'Item', to: '/items/new', icon: 'item', permissions: ['mdm.create', 'mdm.item.create'], keywords: 'product sku' },
  { app: 'master-data', label: 'Customer', to: '/customers/new', icon: 'customer', permissions: ['mdm.create', 'mdm.partner.create'] },
  { app: 'master-data', label: 'Unit of measure', to: '/uoms/new', icon: 'uom', permissions: ['mdm.create', 'mdm.uom.manage'] },
  { app: 'master-data', label: 'Tax code', to: '/tax-codes/new', icon: 'tax', permissions: ['mdm.create', 'mdm.tax.manage', 'accounting.tax-code.create'] },
];
